import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import BusinessOutlinedIcon from "@mui/icons-material/BusinessOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import { useQuery } from "@apollo/client";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyProfiles from "../CompanyProfiles/CompanyProfiles";
import CompanyNotifications from "./CompanyNotifications";
import MyNotifications from "./MyNotifications";
import { GET_USER_DETAILS } from "../../../apis/dashboard-apis/dashboardDetailApi";
import Role from "../../../utils/roles";
import { UserType } from "../../../utils/enums";

const SECTION = { ABOUT: "about", NOTIFICATIONS: "notifications" };

/** Which set of notification preferences is being edited. */
const SCOPE = { COMPANY: "company", MINE: "mine" };

const SECTIONS = [
  {
    key: SECTION.ABOUT,
    label: "About",
    icon: BusinessOutlinedIcon,
    adminOnly: true,
  },
  {
    key: SECTION.NOTIFICATIONS,
    label: "Notifications",
    icon: NotificationsNoneOutlinedIcon,
    adminOnly: false,
  },
];


const CompanySettings = ({ setBreadcrumbLists }) => {
  const theme = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const { data } = useQuery(GET_USER_DETAILS);

  const isAdmin = useMemo(() => {
    const roles = data?.getAccountDetails?.userDetails?.roles;
    if (!Array.isArray(roles)) return false;
    return roles.some((role) => role?.role_description === Role.ADMIN);
  }, [data]);

  const params = new URLSearchParams(location.search);
  const requestedSection = params.get("tab");

  const [section, setSection] = useState(
    requestedSection === SECTION.NOTIFICATIONS
      ? SECTION.NOTIFICATIONS
      : SECTION.ABOUT
  );

  // External users have no employee_data row, so they have no personal
  // preferences to manage.
  const isExternal =
    data?.getAccountDetails?.userDetails?.user_type === UserType.EXTERNAL;

  const [scope, setScope] = useState(SCOPE.COMPANY);

  // The About form edits company-wide details, so non-admins only get
  // Notifications.
  const activeSection = isAdmin ? section : SECTION.NOTIFICATIONS;
  const visibleSections = SECTIONS.filter((item) => isAdmin || !item.adminOnly);

  useEffect(() => {
    setBreadcrumbLists?.([
      "Settings",
      "Company Settings",
      activeSection === SECTION.NOTIFICATIONS ? "Notifications" : "About",
    ]);
  }, [setBreadcrumbLists, activeSection]);

  const handleSelect = (key) => {
    setSection(key);
    navigate(
      key === SECTION.NOTIFICATIONS
        ? "/company-settings?tab=notifications"
        : "/company-settings",
      { replace: true }
    );
  };

  return (
    <Stack direction={{ xs: "column", md: "row" }} sx={{ alignItems: "stretch" }}>
      {/* Left rail */}
      <Box
        sx={{
          width: { xs: "100%", md: 160 },
          flexShrink: 0,
          pr: { md: 2 },
          mb: { xs: 2, md: 0 },
          borderRight: {
            md: `1px solid ${theme.customColors.border.subtle}`,
          },
        }}
      >
        <Typography
          variant="caption"
          sx={{
            display: "block",
            px: 1.5,
            mb: 1,
            fontWeight: 700,
            letterSpacing: "0.06em",
            textTransform: "uppercase",
            color: theme.customColors.text.disabled,
          }}
        >
          Settings
        </Typography>

        {visibleSections.map(({ key, label, icon: Icon }) => {
          const selected = activeSection === key;

          return (
            <Box
              key={key}
              onClick={() => handleSelect(key)}
              role="button"
              tabIndex={0}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleSelect(key);
                }
              }}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                px: 1.5,
                py: 1,
                mb: 0.25,
                borderRadius: "6px",
                cursor: "pointer",
                userSelect: "none",
                backgroundColor: selected
                  ? theme.customColors.background.light
                  : "transparent",
                transition: "background-color 120ms ease",
                "&:hover": {
                  backgroundColor: theme.customColors.background.hover,
                },
              }}
            >
              <Icon
                sx={{
                  fontSize: 18,
                  color: selected
                    ? theme.customColors.text.strong
                    : theme.customColors.text.iconMuted,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: selected ? 600 : 500,
                  color: selected
                    ? theme.customColors.text.strong
                    : theme.customColors.text.secondary,
                }}
              >
                {label}
              </Typography>
            </Box>
          );
        })}
      </Box>

      {/* Content pane */}
      <Box sx={{ flexGrow: 1, minWidth: 0, pl: { md: 3 } }}>
        {activeSection === SECTION.ABOUT && (
          <>
            <CompanyProfiles setBreadcrumbLists={() => {}} />
          </>
        )}

        {activeSection === SECTION.NOTIFICATIONS && (
          <>
            {/* Company vs personal are separate scopes, so they are tabs
                rather than a single scroll. Non-admins only have their own. */}
            {isAdmin && (
              <Tabs
                value={scope}
                onChange={(_event, value) => {
                  // The personal tab stays visible for external users but is
                  // not selectable.
                  if (isExternal && value === SCOPE.MINE) return;
                  setScope(value);
                }}
                sx={{
                  minHeight: 40,
                  mb: 2.5,
                  borderBottom: `1px solid ${theme.customColors.border.subtle}`,
                  "& .MuiTab-root": {
                    minHeight: 40,
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.875rem",
                  },
                }}
              >
                <Tab label="Company" value={SCOPE.COMPANY} />
                <Tab
                  component="div"
                  label={
                    isExternal ? (
                      <Tooltip title="Not available for external users">
                        <span>My notifications</span>
                      </Tooltip>
                    ) : (
                      "My notifications"
                    )
                  }
                  value={SCOPE.MINE}
                  sx={
                    isExternal
                      ? {
                          cursor: "not-allowed",
                          opacity: 0.5,
                          "&:hover": { opacity: 0.5 },
                        }
                      : undefined
                  }
                />
              </Tabs>
            )}

            {isAdmin && scope === SCOPE.COMPANY ? (
              <CompanyNotifications />
            ) : isExternal ? (
              // Reached by a non-admin external user, who gets no tab strip.
              <Alert severity="info">
                Personal notification settings are not available for external
                users.
              </Alert>
            ) : (
              <MyNotifications showHeading={!isAdmin} />
            )}
          </>
        )}
      </Box>
    </Stack>
  );
};

export default CompanySettings;
